import { zodResolver } from "@hookform/resolvers/zod";
import { useWatch } from "react-hook-form";

import {
  AppCreate,
  AppSimpleForm,
  AppFormToolbar,
  AppFormSection,
  SectionHeader,
  AppFormContent,
  AppTextInput,
  AppReferenceInput,
  AppRadioButtonGroupInput,
} from "@/adapters/inbound/layout";
import {
  clientCreateFormSchema,
  type ClientCreateFormInput,
} from "../schemas";
import { toCreateClientCommand } from "../mappers";

export const defaultClientCreateFormValues: ClientCreateFormInput = {
  name: "",
  contactName: "",
  phone: "",
  email: "",
  addressMode: "existing",
  addressId: "",
};

function ClientCreateFields() {
  const addressMode = useWatch<ClientCreateFormInput>({
    name: "addressMode",
  });

  return (
    <>
      <AppTextInput source="name" label="Name" required fullWidth />
      <AppTextInput source="contactName" label="Ansprechpartner" fullWidth />
      <AppTextInput source="phone" label="Telefon" fullWidth />
      <AppTextInput source="email" label="E-Mail" fullWidth />

      <AppRadioButtonGroupInput
        source="addressMode"
        label="Adresse"
        choices={[
          { id: "existing", name: "Bestehende Adresse wählen" },
          { id: "new", name: "Neue Adresse eingeben" },
        ]}
      />

      {addressMode === "existing" ? (
        <AppReferenceInput
          source="addressId"
          reference="addresses"
          label="Adresse"
          isRequired
          autocompleteProps={{
            optionText: (record: any) =>
              [
                record?.label,
                record?.street,
                record?.streetNumber,
                record?.zip,
                record?.city,
              ]
                .filter(Boolean)
                .join(", "),
            fullWidth: true,
          }}
        />
      ) : (
        <>
          <AppTextInput source="address.label" label="Adresszusatz" fullWidth />
          <AppTextInput
            source="address.street"
            label="Straße"
            required
            fullWidth
          />
          <AppTextInput
            source="address.streetNumber"
            label="Nr."
            required
          />
          <AppTextInput source="address.zip" label="PLZ" required />
          <AppTextInput source="address.city" label="Ort" required fullWidth />
          <AppTextInput
            source="address.country"
            label="Land"
            required
            defaultValue="DE"
          />
        </>
      )}
    </>
  );
}

export function ClientCreate() {
  return (
    <AppCreate transform={toCreateClientCommand} redirect="show">
      <AppSimpleForm
        defaultValues={defaultClientCreateFormValues}
        resolver={zodResolver(clientCreateFormSchema) as any}
        mode="onBlur"
        shouldUnregister
        toolbar={<AppFormToolbar showDelete={false} />}
      >
        <AppFormSection>
          <SectionHeader
            title="Kunde anlegen"
            subtitle="Grunddaten erfassen und eine bestehende oder neue Adresse zuordnen."
          />
          <AppFormContent>
            <ClientCreateFields />
          </AppFormContent>
        </AppFormSection>
      </AppSimpleForm>
    </AppCreate>
  );
}
